/* eslint-disable @typescript-eslint/naming-convention */
// React
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { AnalyticsContext, CartOverlaysContext } from "@swiggy-private/checkout-core";
import { INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderLottieContext";

// Utils
import { isEmpty } from "@swiggy-private/js-utils";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";

// Types
import { GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";

// Helpers
import {
    applyFrequencyCappingLogic,
    isStateEqual,
    processCartHeaderLottieData,
    startAnimation,
    updateStateForLottieDisplay,
} from "./helpers";
import { hideTheme } from "@cart-client/hooks/useCartHeaderLottie/helper";
import { useSkipAnimationFinalState } from "./useSkipAnimationFinalState";

// Analytics
import { impressionCartHeaderLottie } from "@cart-client/analytics/events/cartHeaderLottie";

export const useCommonCartHeaderLottie = () => {
    const { cartState } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);
    const { reviewedCart: cartResponse } = cartState;
    const isSuccess = useMemo(() => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS, [cartResponse]);
    const [cartHeaderLottieState, setCartHeaderLottieState] = useState({
        ...INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE.cartHeaderLottieState,
        isStatusSuccess: isSuccess,
    });
    const isLottieRegisteredRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>();
    const prevCartType = useRef<string | undefined>();
    const flag = useRef<boolean>(false);
    const shouldHideTheme = useMemo(() => !isSuccess, [isSuccess]);
    const processedData = useMemo(() => {
        return processCartHeaderLottieData(cartResponse);
    }, [cartResponse]);
    const {
        cartType,
        hasLottie,
        isFrequencyCapEnabled,
        rxId,
        sid,
        sessionFreqCapKey,
        lottieLocalStorageKey,
        lottieFrequencyDataObject,
        frequencyCapData,
        delayData,
        lottieArray,
        isOtg,
        skipAnimationShowFinalState,
        themeConfig,
    } = processedData;
    const prevLottieArrayRef = useRef(lottieArray);
    const sessionCount = useMemo(() => frequencyCapData?.session_count || 0, [frequencyCapData?.session_count]);
    const count = useMemo(() => frequencyCapData?.count || 0, [frequencyCapData?.count]);
    const freqCapId = useMemo(() => frequencyCapData?.id || "", [frequencyCapData?.id]);

    const shouldShowLottie = useMemo(() => !isEmpty(cartType) && hasLottie, [cartType, hasLottie]);
    const hasNecessaryCartData = useMemo(
        () =>
            cartResponse &&
            shouldShowLottie &&
            sid &&
            rxId &&
            (!isFrequencyCapEnabled || !isEmpty(lottieFrequencyDataObject)),
        [cartResponse, shouldShowLottie, sid, rxId, lottieFrequencyDataObject, isFrequencyCapEnabled],
    );

    const shouldHideThemeInitially = useMemo(() => !isSuccess || !shouldShowLottie, [isSuccess, shouldShowLottie]);

    const currentValue = useMemo(
        () => lottieFrequencyDataObject[sessionFreqCapKey],
        [lottieFrequencyDataObject, sessionFreqCapKey],
    );

    const isRxFrequencyCapReached = useMemo(
        () => !isEmpty(cartType) && currentValue === 1 && isSuccess,
        [cartType, currentValue, isSuccess],
    );

    const sendAnalytics = useCallback(() => {
        if (isEmpty(cartType)) {
            return;
        }
        const context = JSON.stringify({
            cart_id: cartResponse?.data?.cartId,
            cartType: cartType,
            is_otg: isOtg,
            address_id: cartResponse?.data?.address_id,
            lotties: lottieArray,
        });
        impressionCartHeaderLottie(impressionEvent, context);
    }, [cartResponse, cartType, impressionEvent, isOtg, lottieArray]);

    useEffect(() => {
        prevLottieArrayRef.current = lottieArray;
    }, [lottieArray]);

    useLayoutEffect(() => {
        if (hasNecessaryCartData && isFrequencyCapEnabled && isRxFrequencyCapReached && hasLottie) {
            setCartHeaderLottieState((prev) => ({
                ...prev,
                cartType: cartType,
                hasLottie: true,
                showAnimation: true,
                themeChange: true,
                showFinalState: true,
                startAnimation: false,
                lottieArray: lottieArray,
                themeConfig: themeConfig,
            }));
            if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
                onCloseOverlay();
            }
        }
    }, [
        cartType,
        enabledOverlayId,
        hasLottie,
        hasNecessaryCartData,
        isFrequencyCapEnabled,
        isRxFrequencyCapReached,
        lottieArray,
        onCloseOverlay,
        themeConfig,
    ]);

    // Handle skipAnimationShowFinalState: Show final state (gradient) directly without animation
    // This is used when magic items lottie has not played yet in the session
    useSkipAnimationFinalState({
        skipAnimationShowFinalState,
        hasLottie,
        cartType,
        lottieArray,
        enabledOverlayId,
        onCloseOverlay,
        setCartHeaderLottieState,
        themeConfig,
    });

    useEffect(() => {
        flag.current = enabledOverlayId === LottieIds.CART_HEADER_LOTTIE;
    }, [enabledOverlayId]);

    useEffect(() => {
        const shouldReset = !isSuccess || cartType !== prevCartType.current;
        if (shouldReset) {
            isLottieRegisteredRef.current = false;
        }
        if (isEmpty(cartType)) {
            setCartHeaderLottieState(() => ({
                ...INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE.cartHeaderLottieState,
                isStatusSuccess: true,
                lottieArray: lottieArray,
                hasLottie: hasLottie,
            }));
        }

        prevCartType.current = cartType;
    }, [cartType, isSuccess, hasLottie, lottieArray]);

    useEffect(() => {
        // Skip if skipAnimationShowFinalState is true - handled by separate useLayoutEffect
        if (
            !cartResponse ||
            isEmpty(cartType) ||
            !hasLottie ||
            (isFrequencyCapEnabled && isRxFrequencyCapReached) ||
            skipAnimationShowFinalState
        ) {
            return;
        }

        const newCartHeaderState = { ...cartHeaderLottieState };
        let isThemeHidden = shouldHideTheme;
        if (shouldHideThemeInitially) {
            // Preconditions for hiding the theme
            Object.assign(newCartHeaderState, hideTheme());
            isThemeHidden = true;
        }

        if (!isThemeHidden && !isLottieRegisteredRef.current) {
            registerOverlays({
                overlayType: OverlayType.LOTTIE,
                priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
                id: LottieIds.CART_HEADER_LOTTIE,
            });
            isLottieRegisteredRef.current = true;
        }
        let updatedState = newCartHeaderState;
        let updatedThemeHidden = isThemeHidden;
        if (flag.current) {
            const frequencyResult = applyFrequencyCappingLogic({
                isThemeHidden,
                isFrequencyCapEnabled,
                lottieLocalStorageKey,
                lottieFrequencyDataObject,
                sid,
                rxId,
                newCartHeaderState,
                sessionCount,
                count,
                freqCapId,
                hasLottie,
                INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE,
                sessionFreqCapKey,
                sendAnalytics,
            });
            if (frequencyResult) {
                updatedState = frequencyResult.updatedState || updatedState;
                updatedThemeHidden = frequencyResult.isThemeHidden || updatedThemeHidden;
                if (updatedThemeHidden) {
                    onCloseOverlay();
                }
            }
        }

        const finalState = updateStateForLottieDisplay({
            newCartHeaderState: updatedState,
            isThemeHidden: updatedThemeHidden,
            cartType,
            hasLottie,
            isSuccess,
            lottieArray,
            themeConfig,
        });
        if (!isStateEqual(cartHeaderLottieState, finalState)) {
            setCartHeaderLottieState(finalState);
        }

        const currentTimeout = timeoutRef.current;
        if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            // clear the timeout if it exists
            if (currentTimeout) {
                clearTimeout(currentTimeout);
            }
            startAnimation({
                newCartHeaderState: finalState,
                setCartHeaderLottieState,
                delayData,
                timeoutRef,
            });
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
        };
    }, [
        cartResponse,
        enabledOverlayId,
        cartHeaderLottieState,
        cartType,
        count,
        delayData,
        freqCapId,
        hasLottie,
        isFrequencyCapEnabled,
        isRxFrequencyCapReached,
        isSuccess,
        lottieArray,
        lottieFrequencyDataObject,
        lottieLocalStorageKey,
        onCloseOverlay,
        registerOverlays,
        rxId,
        sendAnalytics,
        sessionCount,
        sessionFreqCapKey,
        shouldHideTheme,
        shouldHideThemeInitially,
        sid,
        skipAnimationShowFinalState,
        themeConfig,
    ]);

    const cartHeaderContextValue = useMemo(
        () => ({
            cartHeaderLottieState,
            setCartHeaderLottieState,
        }),
        [cartHeaderLottieState],
    );

    return cartHeaderContextValue;
};
